import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { SoftCard } from '@/components/ui/soft-card';
import { hrefFor, presentationFor } from '@/data/localPotential';

import { HideButton } from './HideButton';

const ICON_ACTIVE = {
	primary: 'bg-primary text-primary-foreground',
	action: 'bg-action text-action-foreground',
	success: 'bg-success text-white'
};
const ICON_INACTIVE = {
	primary: 'bg-primary/10 text-primary',
	action: 'bg-action-soft text-action-soft-foreground',
	success: 'bg-success-soft text-success-soft-foreground'
};
const BADGE_ACTIVE = {
	primary: 'bg-primary text-primary-foreground',
	action: 'bg-action text-action-foreground',
	success: 'bg-success text-white'
};
const BADGE_INACTIVE = {
	primary: 'text-primary border border-primary/40',
	action: 'text-action border border-action/40',
	success: 'text-success border border-success/40'
};
const DOT = { primary: 'bg-primary', action: 'bg-action', success: 'bg-success' };

/**
 * A channel / tool suggestion card (NEA-5765), ported from the fresh-dashboard
 * design and bound to a resolver groups[] item ({ id, activated, uplift }).
 * A `comingSoon` item renders a neutral "Coming soon" badge and no CTA (NEA-5935).
 */
export function SuggestionCard({ item, tone = 'primary', onHide, onUnhide, shopPath }) {
	const presentation = presentationFor(item.id);
	const Icon = presentation.icon;
	const activated = item.activated;
	const comingSoon = Boolean(item.comingSoon);
	return (
		<SoftCard className="relative overflow-hidden !p-5 md:!p-6">
			{activated ? <span className="absolute bottom-0 left-0 top-0 w-1 bg-success" aria-hidden /> : null}
			<div className="flex items-start gap-4">
				<div
					className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${activated ? ICON_ACTIVE[tone] : ICON_INACTIVE[tone]}`}
				>
					<Icon className="h-5 w-5" />
				</div>
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<h3 className="text-[15px] font-bold text-foreground md:text-base">{presentation.title}</h3>
						{activated ? (
							<span
								className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-extrabold ${BADGE_ACTIVE[tone]}`}
							>
								<CheckCircle2 className="h-3.5 w-3.5" /> Live · +{item.uplift}%
							</span>
						) : comingSoon ? (
							<span className="inline-flex items-center gap-1 rounded-full border border-border bg-accent/40 px-2.5 py-1 text-xs font-semibold text-muted-foreground">
								Coming soon
							</span>
						) : (
							<span
								className={`inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-semibold ${BADGE_INACTIVE[tone]}`}
							>
								<span aria-hidden className={`h-2 w-2 rounded-full ${DOT[tone]}`} /> +{item.uplift}%
							</span>
						)}
					</div>
					<p className="mt-0.5 text-sm text-muted-foreground">{presentation.body}</p>
				</div>
				{!activated && onHide ? <HideButton onClick={onHide} /> : null}
				{onUnhide ? <HideButton onClick={onUnhide} mode="unhide" /> : null}
				{!comingSoon ? (
					<Link
						to={shopPath(hrefFor(item))}
						className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition ${
							activated
								? 'border border-border bg-white text-foreground hover:bg-accent'
								: 'bg-foreground text-background hover:opacity-90'
						}`}
					>
						{activated ? 'Manage' : 'Learn more'} <ArrowRight className="h-3.5 w-3.5" />
					</Link>
				) : null}
			</div>
		</SoftCard>
	);
}
